import React, { useState } from 'react';
import { CreditCard, BanknoteArrowDown, BanknoteArrowUp } from 'lucide-react';
import { LoanSummaryTable } from './LoanSummaryTable';
import PaymentHistoryChart from './PaymentHistoryChart';
import { getFinancierDashboardDefaultingRateMetrics, getFinancierDashboardMetricsSummary, getFinancierDashboardMonthlyBarChart, getFinancierDashboardMonthlyTrendChart, getFinancierDashboardProductDistributionChart } from '../../../../api/requests';
import { useQuery } from '@tanstack/react-query';
import { formatCurrencyAmount, formatNumberValue, getGreeting } from '../../../../utils/miscFunctions';
import useUserAccount from '../../../../hooks/userAccountHook';
import { useNavigate } from 'react-router';
import useRoute from '../../../../hooks/routesHook';
import SelectMenu from '../../../../components/form-control/SelectMenu';

interface FinancePartnerDashboardProps {
  isCompany?: boolean;
}

type MetricsSummary = {
  monthlyExpectedRepaymentAmount: number;
  monthlyPaidRepaymentAmount: number;
  totalDisbursementAmount: number;
  totalDisbursementVolume: number;
  totalLoanAmount: number;
  totalLoanVolume: number;
}

type monthlyBarChartType = {
  disbursementData: number[];
  expectedRepaymentData: number[];
  labels: string[];
  loanData: number[];
  paidRepaymentData: number[];
}

const FinancePartnerDashboard: React.FC<FinancePartnerDashboardProps> = ({ isCompany }) => {
  const [monthlyTrend, setMonthlyTrend] = useState<any>();
  const [monthlyBarChart, setMonthlyBarChart] = useState<monthlyBarChartType>();
  const [productDistributionChart, setProductDistributionChart] = useState<any>();
  const [metricsSummary, setMetricsSummary] = useState<MetricsSummary | null>(null);
  // Year filter drives the flow charts (e.g. monthly disbursement/repayment bar chart).
  const [selectedYear, setSelectedYear] = useState<string>(String(new Date().getFullYear()));
  const { user } = useUserAccount();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();

  // const getDisbursementMetrics = async () => {
  //   try {
  //     const response = await getFinancierDashboardDisbursementMetrics();
  //     return (response);
  //   } catch (error) {
  //     console.error('Error fetching disbursement metrics:', error);
  //   }
  // };


  // const getLoanMetrics = async () => {
  //   try {
  //     const response = await getFinancierDashboardLoanMetrics();
  //     return response;
  //   } catch (error) {
  //     console.error('Error fetching loan metrics:', error);
  //   }
  // };

  const getProductDistributtionChart = async () => {

    try {
      const response: any = await getFinancierDashboardProductDistributionChart();
      setProductDistributionChart(response?.data);
    } catch (error) {
      console.error('Error fetching product distribution chart:', error);
    }
  };

  const getMonthlyTrendChart = async () => {

    try {
      const response: any = await getFinancierDashboardMonthlyTrendChart();
      setMonthlyTrend(response?.data);
    } catch (error) {
      console.error('Error fetching monthly trend:', error);
    }
  };

  const getMetricsSummary = async () => {

    try {
      const response: any = await getFinancierDashboardMetricsSummary();
      if (response) {

        setMetricsSummary(response?.data);
      }
    } catch (error) {
      console.error('Error fetching metrics summary:', error);
    }
  };

  const getMonthlyBarChart = async () => {
    try {
      const response: any = await getFinancierDashboardMonthlyBarChart(Number(selectedYear));
      setMonthlyBarChart(response?.data);
    } catch (error) {
      console.error('Error fetching monthly bar chart:', error);
    }
  };

  const getDefaultingRateMetrics = async () => {
    try {
      const response: any = await getFinancierDashboardDefaultingRateMetrics();
      return response?.data;
    } catch (error) {
      console.error('Error fetching defaulting rate metrics:', error);
    }
  };

  const routeToRepayments = () => navigate(`${getDashboardBase()}/repayments`);
  const routeToLoans = () => navigate(`${getDashboardBase()}/loans`);
  const routeToDisbursements = () => navigate(`${getDashboardBase()}/disbursements`);

  useQuery({
    queryKey: ['getMonthlyBarChart', selectedYear],
    queryFn: getMonthlyBarChart,
  })

  const { data: defaultingRateMetrics } = useQuery({
    queryKey: ['getDefaultingRateMetrics'],
    queryFn: getDefaultingRateMetrics,
  })

  useQuery({
    queryKey: ['getMetricsSummary'],
    queryFn: getMetricsSummary,
  })

  useQuery({
    queryKey: ['getMonthlyTrendChart'],
    queryFn: getMonthlyTrendChart,
  })

  useQuery({
    queryKey: ['getProductDistributtionChart'],
    queryFn: getProductDistributtionChart,
  })

  // useQuery({
  //   queryKey: ['getLoanMetrics'],
  //   queryFn: getLoanMetrics,
  // })

  // useQuery({
  //   queryKey: ['getDisbursementMetrics'],
  //   queryFn: getDisbursementMetrics,
  // })

  return (
    <div className="space-y-8 dark:bg-darkBg">
      {/* Greeting and Summary */}
      <div className="bg-white dark:border border dark:border-darkBg-border shadow rounded-lg p-6 dark:bg-darkBg">
        {!isCompany && <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-darkBg-mainText">{getGreeting()}{user?.firstName ? `, ${user?.firstName}` : ''}</h2>
          </div>
        </div>}

        <div className="flex justify-end mt-4">
          <div className="w-40">
            <SelectMenu
              label=""
              name="years"
              listSelectedValue={selectedYear}
              onChange={(val: string) => setSelectedYear(val)}
              list={['2024', '2025', '2026']}
            />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 dark:bg-darkBg dark:border-darkBg-border flex items-center cursor-pointer" onClick={routeToDisbursements}>
            <div className="bg-blue-100 p-3 rounded-full">
              <BanknoteArrowUp className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Total Disbursed Amount</div>
              <div className="text-xl font-semibold text-blue-700">
                {formatCurrencyAmount(metricsSummary?.totalDisbursementAmount)}
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200 dark:bg-darkBg dark:border-darkBg-border flex items-center cursor-pointer" onClick={routeToRepayments}>
            <div className="bg-yellow-100 p-3 rounded-full">
              <BanknoteArrowDown className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Total Repaid Amount</div>
              <div className="text-xl font-semibold text-yellow-700">
                {formatCurrencyAmount(metricsSummary?.monthlyPaidRepaymentAmount)}
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded-lg border border-green-200 dark:bg-darkBg dark:border-darkBg-border flex items-center cursor-pointer" onClick={routeToRepayments}>
            <div className="bg-green-100 p-3 rounded-full">
              <CreditCard className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Total Outstanding Amount</div>
              <div className="text-xl font-semibold text-green-700">
                {formatCurrencyAmount(metricsSummary?.monthlyExpectedRepaymentAmount)}
              </div>
            </div>
          </div>

          <div className="bg-violet-50 p-4 rounded-lg border border-violet-200 dark:bg-darkBg dark:border-darkBg-border flex items-center cursor-pointer" onClick={routeToLoans}>
            <div className="bg-violet-100 p-3 rounded-full">
              <CreditCard className="h-6 w-6 text-violet-600" />
            </div>
            <div className="ml-4">
              <div className="text-sm font-medium text-gray-500">Pending Requests</div>
              <div className="text-xl font-semibold text-violet-700">
                {formatNumberValue(metricsSummary?.totalLoanVolume)} <span className="text-sm font-normal">{metricsSummary?.totalLoanVolume === 1 ? 'loan' : 'loans'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      {monthlyBarChart && monthlyBarChart?.labels?.length > 0 && (
        <PaymentHistoryChart
          metricsSummary={metricsSummary}
          labels={monthlyBarChart?.labels || []}
          loans={monthlyBarChart?.paidRepaymentData || []}
          paidRepaymentData={monthlyBarChart?.paidRepaymentData || []}
          expectedRepaymentData={monthlyBarChart?.expectedRepaymentData || []}
          productData={productDistributionChart?.chart}
          trendData={monthlyTrend}
          chartOption1='Expected Repayments'
          chartOption2='Paid Repayments'
          defaultingRateMetrics={defaultingRateMetrics}
        />
      )}

      {/* <RepaymentMonthlyTrend /> */}

      {/* Loans and Payments Grid */}
      <div className="">
        <LoanSummaryTable />

      </div>
    </div>
  );
};


export default FinancePartnerDashboard;
